import { createSlice } from '@reduxjs/toolkit'
import {
  escapeCrashBet, getAllRoundHistory, getMyBets, getMyBetsPaginated, getPreviousRoundBets, getTopBets,
  placedBetCrashGame
} from 'games/CrashGames/slice-thunk/crashGame.thunk'
import { APPEND_TYPES, DEFAULT_LIVE_STATS, DEFAULT_MY_BETS_LIMIT } from 'games/CrashGames/GameTheme/FlyCrashGame/crashgame.constants'
import { plus } from 'number-precision'
import { BET_RESULT } from 'utils/constants/index'
import { moment } from 'config/moment.config'

const initialState = {
  roundBetResult: null,
  betHistoryData: [],
  resultData: [],
  currentRoundId: '',
  placingOrCashOutLoading: [false, false],
  placedCrashBetId: [null, null],
  currentPlacedBet: [null, null],
  allRoundHistoryData: [],
  allRoundLoading: false,
  myBetsData: {
    count: 0,
    rows: []
  },
  myBetsDataPaginated: {
    count: 0,
    rows: []
  },
  myBetsDataPaginatedLoading: false,
  myBetsLoading: false,
  topBetsData: [],
  topBetsLoading: false,
  playerStats: null,
  chooseCharacterOpen: true,
  provableFairData: null,
  previousBetsData: [],
  betSelectedHistoryData: null,
  wins: 0,
  losses: 0,
  profit: 0,
  wagered: 0,
  liveStatsData: DEFAULT_LIVE_STATS,
  winningPopupDetails: null,
  demoLivePlacedBet: [],
  demoMyBets: []
}

const {
  actions: {
    setPlacedBetsData,
    setResultData,
    setCurrentRoundId,
    setPlacedCrashBetId,
    setChooseCharacterOpen,
    appendCurrentBetsToMyBets,
    setPlacingOrCashoutLoading,
    setCurrentPlacedBet,
    setBetSelectHistoryData,
    setCrashGameWins,
    setCrashGameLosses,
    setCrashGameProfit,
    setCrashGameWagered,
    setLiveStatsData,
    resetLiveStats,
    resetWinningPopupDetails,
    setProvableFairData,
    setDemoLivePlacedBets,
    setDemoMyBets
  },
  reducer
} = createSlice({
  name: 'crashGame',
  initialState,
  reducers: {
    setBetSelectHistoryData: (state, action) => ({
      ...state,
      betSelectedHistoryData: action.payload
    }),
    setPlacingOrCashoutLoading: (state, action) => {
      if (action.payload?.index !== undefined && action.payload?.value !== undefined) {
        const index = action.payload?.index ?? 0
        const _loading = [...state.placingOrCashOutLoading]
        _loading[index] = action.payload.value
        return {
          ...state,
          placingOrCashOutLoading: _loading
        }
      }
      return state
    },
    resetWinningPopupDetails: (state, action) => {
      return {
        ...state,
        winningPopupDetails: null
      }
    },
    setPlacedBetsData: (state, action) => {
      return {
        ...state,
        betHistoryData: action.payload
      }
    },
    setResultData: (state, action) => {
      return {
        ...state,
        resultData: action.payload
      }
    },
    setCurrentRoundId: (state, action) => {
      return {
        ...state,
        currentRoundId: action.payload
      }
    },
    setChooseCharacterOpen: (state, action) => {
      return {
        ...state,
        chooseCharacterOpen: action.payload
      }
    },
    setProvableFairData: (state, action) => {
      return {
        ...state,
        provableFairData: action.payload
      }
    },
    setCurrentPlacedBet: (state, action) => {
      if (!action.payload?.reset) {
        const { id, betDetails } = action.payload
        const newPlacedCrashBetId = [...state.currentPlacedBet]
        newPlacedCrashBetId[id] = betDetails
        return {
          ...state,
          currentPlacedBet: newPlacedCrashBetId
        }
      } else {
        return {
          ...state,
          currentPlacedBet: initialState.currentPlacedBet
        }
      }
    },
    setPlacedCrashBetId: (state, action) => {
      if (!action.payload?.reset) {
        const { id, betId } = action.payload
        const newPlacedCrashBetId = [...state.placedCrashBetId]
        newPlacedCrashBetId[id] = betId
        return {
          ...state,
          placedCrashBetId: newPlacedCrashBetId
        }
      } else {
        return {
          ...state,
          placedCrashBetId: initialState.placedCrashBetId
        }
      }
    },
    appendCurrentBetsToMyBets: (state, action) => {
      if (action.payload?.type === APPEND_TYPES.ESCAPE) {
        const escapedBet = action.payload.data
        const betExist = state.myBetsData?.rows?.some(bet => bet?.id === escapedBet?.[0]?.id)
        if (!betExist) {
          return {
            ...state,
            winningPopupDetails: { ...escapedBet?.[0] },
            myBetsData: {
              count: state.myBetsData.count + (escapedBet?.length ?? 0),
              rows: [...escapedBet, ...state.myBetsData?.rows].slice(0, DEFAULT_MY_BETS_LIMIT)
            }
          }
        }
      } else if (action.payload?.type === APPEND_TYPES.ROUND_STOPPED) {
        const userId = action.payload?.userId
        if (userId) {
          const alreadyPresentBetIds = state.myBetsData?.rows.map(bet => +bet.id)
          const currentPlacedBets = [...state.betHistoryData].filter(bet => (+bet?.userId === +userId && !alreadyPresentBetIds.includes(+bet.id)))
          return {
            ...state,
            myBetsData: {
              count: state.myBetsData.count + (currentPlacedBets?.length ?? 0),
              rows: [...currentPlacedBets, ...state.myBetsData?.rows].slice(0, DEFAULT_MY_BETS_LIMIT)
            }
          }
        }
      }
      return state
    },
    setCrashGameWins: (state, action) => {
      if (action.payload) {
        return {
          ...state,
          wins: action.payload.reset
        }
      }
      return {
        ...state,
        wins: state.wins + 1
      }
    },
    setCrashGameLosses: (state, action) => {
      if (action.payload) {
        return {
          ...state,
          losses: action.payload.reset
        }
      }
      return {
        ...state,
        losses: state.losses + 1
      }
    },
    setCrashGameProfit: (state, action) => {
      return {
        ...state,
        profit: plus(state.profit, action.payload)
      }
    },
    setCrashGameWagered: (state, action) => {
      return {
        ...state,
        wagered: plus(state.wagered, action.payload)
      }
    },

    setLiveStatsData: (state, action) => {
      const liveData = { ...state.liveStatsData }
      const { betAmount, winningAmount, result } = action.payload || {}
      if (!result || (result !== 'won' && result !== 'lost')) {
        return // Handle invalid result values
      }

      if (result === 'won') {
        liveData.wins = (liveData.wins || 0) + 1
      } else if (result === 'lost') {
        liveData.losses = (liveData.losses || 0) + 1
      }

      if (!isNaN(parseFloat(betAmount))) {
        liveData.wagered = (parseFloat(liveData.wagered || '0') + parseFloat(betAmount)).toFixed(2)
      }

      if (!isNaN(parseFloat(winningAmount)) && !isNaN(parseFloat(betAmount)) && result !== 'push') {
        const profitDelta = (parseFloat(winningAmount) - parseFloat(betAmount)).toFixed(2)
        liveData.profit = (parseFloat(liveData.profit || '0') + parseFloat(profitDelta)).toFixed(2)
      }
      return ({
        ...state,
        liveStatsData: liveData
      })
    },
    resetLiveStats: (state, action) => {
      return {
        ...state,
        wins: 0,
        losses: 0,
        profit: 0,
        wagered: 0
      }
    },
    setDemoLivePlacedBets: (state, action) => {
      let betsList = initialState.demoLivePlacedBet
      if (action.payload.operation === 'SET') {
        betsList = [...state.demoLivePlacedBet, ...action.payload?.bets]
      } else if (action.payload.operation === 'RESET') {
        betsList = initialState.demoLivePlacedBet
      } else if (action.payload.operation === 'REPLACE') {
        const updatedBets = state.demoLivePlacedBet.map(bet => {
          if (bet.id === action.payload.bets[0].id) {
            return action.payload.bets[0]
          }
          return bet
        })
        betsList = [...updatedBets]
      } else if (action.payload.operation === 'REMOVE') {
        const updatedBets = state.demoLivePlacedBet.filter(bet => (bet.id !== action.payload.bets[0].id))
        betsList = [...updatedBets]
      }
      return ({
        ...state,
        demoLivePlacedBet: betsList
      })
    },
    setDemoMyBets: (state, action) => {
      const updatedBets = state.demoLivePlacedBet.map(bet => {
        const newBet = { ...bet }
        if (newBet.result === 'pending') {
          newBet.result = BET_RESULT.LOST
          newBet.updatedAt = moment().format()
        }
        return newBet
      })
      return ({
        ...state,
        demoMyBets: [...updatedBets, ...state.demoMyBets]
      })
    }
  },
  extraReducers: (builder) => {
    builder
      .addCase(placedBetCrashGame.pending, (state, action) => {
        return {
          ...state
        }
      })
      .addCase(placedBetCrashGame.rejected, (state, action) => {
        return {
          ...state
        }
      })
      .addCase(escapeCrashBet.rejected, (state, action) => ({
        ...state,
        roundBetResult: null
      }))
      .addCase(escapeCrashBet.pending, (state, action) => ({
        ...state
      }))
      .addCase(getAllRoundHistory.fulfilled, (state, action) => {
        return {
          ...state,
          allRoundHistoryData: action.payload?.history,
          allRoundLoading: false
        }
      })
      .addCase(getAllRoundHistory.rejected, (state, action) => {
        return {
          ...state,
          allRoundHistoryData: [],
          allRoundLoading: false
        }
      })

      .addCase(getAllRoundHistory.pending, (state, action) => {
        return {
          ...state,
          allRoundHistoryData: action.payload,
          allRoundLoading: true
        }
      })
      .addCase(getMyBets.pending, (state, action) => {
        return {
          ...state,
          myBetsLoading: true

        }
      })
      .addCase(getMyBets.rejected, (state, action) => {
        return {
          ...state,
          myBetsData: initialState.myBetsData,
          myBetsLoading: false

        }
      })
      .addCase(getMyBets.fulfilled, (state, action) => {
        return {
          ...state,
          myBetsData: {
            count: action.payload.count,
            rows: action.payload.rows.map(betDetail => ({ ...betDetail, roundId: betDetail.crashGameRoundDetail.roundId }))
          },
          myBetsLoading: false
        }
      })
      .addCase(getMyBetsPaginated.pending, (state, action) => {
        return {
          ...state,
          myBetsDataPaginatedLoading: true

        }
      })
      .addCase(getMyBetsPaginated.rejected, (state, action) => {
        return {
          ...state,
          myBetsDataPaginated: initialState.myBetsDataPaginated,
          myBetsDataPaginatedLoading: false

        }
      })
      .addCase(getMyBetsPaginated.fulfilled, (state, action) => {
        return {
          ...state,
          myBetsDataPaginated: action.payload,
          myBetsDataPaginatedLoading: false

        }
      })
      .addCase(getPreviousRoundBets.pending, (state, action) => {
        return {
          ...state
        }
      })
      .addCase(getPreviousRoundBets.rejected, (state, action) => {
        return {
          ...state,
          previousBetsData: []

        }
      })
      .addCase(getPreviousRoundBets.fulfilled, (state, action) => {
        return {
          ...state,
          previousBetsData: action.payload
        }
      })
      .addCase(getTopBets.pending, (state, action) => {
        return {
          ...state,
          topBetsLoading: true
        }
      })
      .addCase(getTopBets.fulfilled, (state, action) => {
        return {
          ...state,
          topBetsData: action.payload,
          topBetsLoading: false
        }
      })
      .addCase(getTopBets.rejected, (state, action) => {
        return {
          ...state,
          topBetsData: [],
          topBetsLoading: false
        }
      })
  }
})

export {
  setPlacedBetsData,
  setResultData,
  setCurrentRoundId,
  setPlacedCrashBetId,
  setChooseCharacterOpen,
  appendCurrentBetsToMyBets,
  setPlacingOrCashoutLoading,
  setCurrentPlacedBet,
  setBetSelectHistoryData,
  setCrashGameWins,
  setCrashGameLosses,
  setCrashGameProfit,
  setCrashGameWagered,
  setLiveStatsData,
  resetLiveStats,
  resetWinningPopupDetails,
  setProvableFairData,
  setDemoLivePlacedBets,
  setDemoMyBets
}
export default reducer
